import React, { useState } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import config from '../../config';

const API_ENDPOINT = config.api.url;

// Read a File/Blob as a base64-encoded string (no data: prefix).
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
  });
}

// Small reusable field for image references. Lets the admin either
//   (a) upload a file via the /upload endpoint, or
//   (b) paste an existing URL.
// Renders a small preview when a URL is set.
export default function ImageUrlField({ value, onChange, label }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const base64 = await fileToBase64(file);
      const resp = await fetch(`${API_ENDPOINT}upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // The upload lambda currently doesn't enforce this header but we
          // send it to match what the rich-text editor sends.
          'Authorization': `Bearer ${localStorage.getItem('idToken') || ''}`,
        },
        body: JSON.stringify({
          fileContent: base64,
          fileName: file.name,
          fileType: file.type || 'application/octet-stream',
        }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.fileUrl) {
        throw new Error(data.error || `Upload failed (${resp.status})`);
      }
      onChange(data.fileUrl);
    } catch (err) {
      console.error('Image upload failed:', err);
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      // reset input so the same file can be picked again
      e.target.value = '';
    }
  }

  return (
    <div className="image-url-field">
      {label ? <Form.Label>{label}</Form.Label> : null}
      {value ? (
        <div className="image-url-field__preview" style={{ marginBottom: 6 }}>
          <img
            src={value}
            alt=""
            style={{ maxHeight: 60, maxWidth: 160, objectFit: 'contain', border: '1px solid #eee', borderRadius: 4, padding: 4 }}
          />
          <Button
            variant="link"
            size="sm"
            onClick={() => onChange('')}
            style={{ marginLeft: 8, padding: 0 }}
          >
            Remove
          </Button>
        </div>
      ) : null}
      <Form.Control
        type="file"
        accept="image/*"
        onChange={handleFile}
        disabled={uploading}
        size="sm"
        className="mb-1"
      />
      <Form.Control
        type="text"
        placeholder="…or paste an image URL"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        size="sm"
      />
      {uploading ? (
        <div style={{ marginTop: 4, color: '#666', fontSize: '0.85rem' }}>
          <Spinner as="span" animation="border" size="sm" className="me-2" />Uploading…
        </div>
      ) : null}
      {error ? (
        <div style={{ marginTop: 4, color: '#b00020', fontSize: '0.85rem' }}>
          {error}. You can paste the image URL directly instead.
        </div>
      ) : null}
    </div>
  );
}
